/** Tab bar + body switcher for the Contract Detail page.
 *
 * Reads agent_results directly so each tab renders the same rich body
 * the reviewer saw during the HIL flow. The per-tab body (and the
 * gate-centric review actions) live in ContractStageBody.
 */
import { useState } from 'react';
import { ContractStageBody } from './ContractStageBody';
import { extractReviewerNote } from '@/pages/jobs/stages/StageHistoryRowHelpers';
import type { ContractDetail } from './types';
import type { AgentResult, Job, GateReport } from '@/types';

const TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'document_assistant', label: 'OCR Verification' },
  { key: 'document', label: 'Document' },
  { key: 'parties', label: 'Parties' },
  { key: 'clause', label: 'Clauses' },
  { key: 'gate_1', label: 'Gate 1' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'signature', label: 'Signature' },
  { key: 'gate_2', label: 'Gate 2' },
  { key: 'gate', label: 'Gate (Final)' },
  { key: 'orchestrator_bootstrap', label: 'Final Report' },
] as const;

export type ContractStageTabKey = (typeof TABS)[number]['key'];
type TabKey = ContractStageTabKey;

export function ContractStageTabs({
  contract, agents, job, report, onActivity, onCommentSaved,
  active: activeProp, onActiveChange, onEditMetadata,
}: {
  contract: ContractDetail;
  agents: AgentResult[];
  /** Underlying pipeline job — powers the Final-gate binding decision. */
  job: Job | null;
  /** Gate report — required for the Final-gate decision; null until synth. */
  report: GateReport | null;
  /** Fired after a view-only action (translate, etc.) succeeds so the
   *  Contract Detail page can refresh its activity feed. */
  onActivity?: () => void;
  /** Fired after a reviewer comment/approval is saved so the page can
   *  re-fetch agents and the dashboard's purple ring picks up the change. */
  onCommentSaved?: () => void;
  /** Optional controlled active tab — lets the page's top bar jump here
   *  (e.g. "Go to review" → Final gate). Falls back to internal state. */
  active?: TabKey;
  onActiveChange?: (key: TabKey) => void;
  /** Opens the metadata editor — surfaced on the Final Report tab. */
  onEditMetadata?: () => void;
}) {
  const [activeState, setActiveState] = useState<TabKey>('summary');
  const active = activeProp ?? activeState;
  const setActive = (key: TabKey) => {
    if (onActiveChange) onActiveChange(key);
    else setActiveState(key);
  };
  const agentNames = new Set(agents.map((a) => a.agent_name));
  // Map of stage → whether the reviewer attached a note on this contract.
  // Drives the small violet dot on the tab button so the reviewer can
  // see which stages have prior context without clicking each one.
  const stagesWithNote = new Set(
    agents
      .filter((a) => extractReviewerNote(a.edits_json ?? null))
      .map((a) => a.agent_name),
  );

  const hasData = (key: TabKey) => key === 'summary' || agentNames.has(key);
  // Keyboard navigation only visits tabs that have content — empty stages are
  // disabled and skipped, matching what a click can reach.
  const enabledKeys = TABS.map((t) => t.key).filter(hasData);

  function moveTo(key: TabKey) {
    setActive(key);
    document.getElementById(`stage-tab-${key}`)?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    const idx = enabledKeys.indexOf(active);
    if (idx === -1) return;
    let next: TabKey | null = null;
    if (e.key === 'ArrowRight') next = enabledKeys[(idx + 1) % enabledKeys.length];
    else if (e.key === 'ArrowLeft') next = enabledKeys[(idx - 1 + enabledKeys.length) % enabledKeys.length];
    else if (e.key === 'Home') next = enabledKeys[0];
    else if (e.key === 'End') next = enabledKeys[enabledKeys.length - 1];
    if (next) { e.preventDefault(); moveTo(next); }
  }

  return (
    <div className="space-y-3">
      <div role="tablist" aria-label="Contract stages" className="flex items-center gap-1 border-b overflow-x-auto">
        {TABS.map((t) => {
          const enabled = hasData(t.key);
          const hasNote = t.key !== 'summary' && stagesWithNote.has(t.key);
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              id={`stage-tab-${t.key}`}
              role="tab"
              aria-selected={isActive}
              aria-controls="stage-tabpanel"
              tabIndex={isActive ? 0 : -1}
              disabled={!enabled}
              onClick={() => { setActive(t.key); }}
              onKeyDown={handleKeyDown}
              className={`px-3 py-1.5 text-xs whitespace-nowrap border-b-2 transition-colors inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50 ${
                isActive
                  ? 'border-primary text-foreground font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
              {hasNote && (
                <span
                  aria-label="has reviewer note"
                  className="inline-block h-1.5 w-1.5 rounded-full bg-violet-500"
                />
              )}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id="stage-tabpanel"
        aria-labelledby={`stage-tab-${active}`}
        tabIndex={0}
      >
        <ContractStageBody
          active={active}
          contract={contract}
          agents={agents}
          job={job}
          report={report}
          jobId={contract.job_id ?? null}
          onActivity={onActivity}
          onCommentSaved={onCommentSaved}
          onEditMetadata={onEditMetadata}
        />
      </div>
    </div>
  );
}
